<template>
  <div
    ref="container"
    :class="[$style.base, { [$style.smallScreen]: $screen === 's' }]"
  >
    <iframe
      v-if="containerHeight !== null"
      ref="iframe"
      :style="$screen === 's' ? { height: `${containerHeight - headerHeight}px`, top: `${headerHeight}px` } : {}"
      :src="widgetPreviewUrl"
      v-test="'booking-widget-preview-iframe'"
    />
  </div>
</template>

<script
  setup
  lang="ts"
>
import { onClickOutside, useResizeObserver } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, ref, useTemplateRef } from 'vue';
import config from '@/config';
import { useCompanyStore } from '@/stores/company';
import { useMicroSiteStore } from '@/stores/micro-site';
import { usePageLayoutStore } from '@/stores/page-layout';

const { company } = useCompanyStore();
const { bookingWidgetColor } = storeToRefs(useMicroSiteStore());

const props = withDefaults(
  defineProps<{
    showMobilePreview?: boolean;
  }>(),
  {
    showMobilePreview: false,
  },
);

const emit = defineEmits<{
  closed: [];
}>();

const { headerHeight } = storeToRefs(usePageLayoutStore());

const container = useTemplateRef<HTMLDivElement>('container');
const containerWidth = ref<number | null>(null);
const containerHeight = ref<number | null>(null);

useResizeObserver(container, (entries) => {
  const entry = entries[0];
  const { width, height } = entry.contentRect;
  containerWidth.value = Math.floor(width);
  containerHeight.value = Math.floor(height);
});

const widgetPreviewUrl = computed(() => {
  let baseUrl = `${config.bookingWidgetUrl}/?widget=booking&company=${company.publicId}`;
  if (props.showMobilePreview) {
    const width = containerWidth.value || 350;
    const height = Math.max((containerHeight.value || 500) - 50, 450);
    baseUrl += `&width=${width}&height=${height}&preview-mode=true`;
  } else {
    const width = 550;
    const height = Math.min((containerHeight.value || 500) - 50, 700);
    baseUrl += `&outline=shadow&width=${width}&height=${height}&inline=true&standalone=true`;
  }
  if (bookingWidgetColor.value) {
    baseUrl += `&color=${bookingWidgetColor.value}`;
  }
  return baseUrl;
});

const iframe = useTemplateRef<HTMLIFrameElement>('iframe');
onClickOutside(iframe, () => {
  emit('closed');
});

const handlePostMessage = (event: MessageEvent) => {
  if (event.source !== iframe.value?.contentWindow) {
    return;
  }

  if (event.data?.type === 'widget-hidden') {
    emit('closed');
  }
};

onMounted(() => {
  window.addEventListener('message', handlePostMessage);
});

onUnmounted(() => {
  window.removeEventListener('message', handlePostMessage);
});
</script>

<style
  lang="scss"
  module
>
  .base {
    overflow: hidden;
    flex-shrink: 0;

    iframe {
      border: 0;
      width: 100%;
      height: 100%;
      position: relative;
    }

    &.smallScreen {
      position: fixed;
      top: 0;
      left: 0;
      height: 100vh;
      width: 100vw;
      z-index: 999;
    }

    &:not(.smallScreen) {
      width: 500px;
      height: 78vh;
    }
  }
</style>
